// src/pages/trading-portal/OptionsContractManagement/PositionManagementPage.js
// Options Position Management page with 6-tab interface

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Autocomplete,
    Tabs,
    Tab,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Calculate as CalculateIcon,
    Visibility as ViewIcon,
    TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    StrategyType, StrategyTypeLabels, StrategyTypeColors,
    PositionStatus, PositionStatusLabels, PositionStatusColors,
    LegSide, LegSideLabels, LegSideColors,
    OptionType, OptionTypeLabels, OptionTypeColors,
    MarginMethodology, MarginMethodologyLabels,
    RollStatus, RollStatusLabels, RollStatusColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const PositionManagementPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared
    const [symbolOptions, setSymbolOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [selectedPositionId, setSelectedPositionId] = useState(null);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // ==================== TAB 0: ACTIVE POSITIONS ====================
    const [positionsData, setPositionsData] = useState([]);
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [positionsPage, setPositionsPage] = useState(0);
    const [positionsPerPage, setPositionsPerPage] = useState(10);
    const [positionsTotal, setPositionsTotal] = useState(0);
    const [positionsSearch, setPositionsSearch] = useState('');
    const [positionsStatusFilter, setPositionsStatusFilter] = useState('');
    const [positionsStrategyFilter, setPositionsStrategyFilter] = useState('');
    const [positionsDialog, setPositionsDialog] = useState(false);
    const [positionsForm, setPositionsForm] = useState({});
    const [positionsEditing, setPositionsEditing] = useState(null);
    const [positionsDeleteDialog, setPositionsDeleteDialog] = useState(false);
    const [positionsDeleteTarget, setPositionsDeleteTarget] = useState(null);
    const [formLegs, setFormLegs] = useState([]);
    const [refreshingGreeksId, setRefreshingGreeksId] = useState(null);

    // ==================== TAB 1: POSITION DETAIL ====================
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [legDialog, setLegDialog] = useState(false);
    const [legForm, setLegForm] = useState({});
    const [legEditing, setLegEditing] = useState(null);
    const [legDeleteDialog, setLegDeleteDialog] = useState(false);
    const [legDeleteTarget, setLegDeleteTarget] = useState(null);

    // ==================== TAB 2: P&L ATTRIBUTION ====================
    const [pnlData, setPnlData] = useState([]);
    const [pnlLoading, setPnlLoading] = useState(false);
    const [pnlPage, setPnlPage] = useState(0);
    const [pnlPerPage, setPnlPerPage] = useState(10);
    const [pnlTotal, setPnlTotal] = useState(0);
    const [pnlPositionId, setPnlPositionId] = useState(null);
    const [pnlCalcDialog, setPnlCalcDialog] = useState(false);
    const [pnlCalcForm, setPnlCalcForm] = useState({});

    // ==================== TAB 3: MARGIN REQUIREMENTS ====================
    const [marginPositionId, setMarginPositionId] = useState(null);
    const [marginData, setMarginData] = useState(null);
    const [marginLoading, setMarginLoading] = useState(false);
    const [marginCalcDialog, setMarginCalcDialog] = useState(false);
    const [marginCalcForm, setMarginCalcForm] = useState({});

    // ==================== TAB 4: ROLL MANAGEMENT ====================
    const [rollData, setRollData] = useState([]);
    const [rollLoading, setRollLoading] = useState(false);
    const [rollPage, setRollPage] = useState(0);
    const [rollPerPage, setRollPerPage] = useState(10);
    const [rollTotal, setRollTotal] = useState(0);
    const [rollStatusFilter, setRollStatusFilter] = useState('');
    const [rollEvalDialog, setRollEvalDialog] = useState(false);
    const [rollEvalPositionId, setRollEvalPositionId] = useState(null);
    const [evaluatingRoll, setEvaluatingRoll] = useState(false);
    const [rollStatusDialog, setRollStatusDialog] = useState(false);
    const [rollStatusTarget, setRollStatusTarget] = useState(null);
    const [rollStatusValue, setRollStatusValue] = useState('');

    // ==================== TAB 5: ASSIGNMENT MONITOR ====================
    const [assignmentData, setAssignmentData] = useState([]);
    const [assignmentLoading, setAssignmentLoading] = useState(false);
    const [assignmentPage, setAssignmentPage] = useState(0);
    const [assignmentPerPage, setAssignmentPerPage] = useState(10);
    const [assignmentTotal, setAssignmentTotal] = useState(0);
    const [assignmentPositionFilter, setAssignmentPositionFilter] = useState(null);
    const [assignmentCheckDialog, setAssignmentCheckDialog] = useState(false);
    const [assignmentCheckPositionId, setAssignmentCheckPositionId] = useState(null);
    const [checkingAssignment, setCheckingAssignment] = useState(false);

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, add, edit, del] = await Promise.all([
                hasPermission('tr-ocm-positions', 'view'),
                hasPermission('tr-ocm-positions', 'add'),
                hasPermission('tr-ocm-positions', 'edit'),
                hasPermission('tr-ocm-positions', 'delete'),
            ]);
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPerms();
        fetchSymbols();
        fetchPositionOptions();
    }, []);

    // ==================== FETCH SYMBOLS ====================
    const fetchSymbols = async () => {
        try {
            const res = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            setSymbolOptions(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    };

    // ==================== FETCH POSITION OPTIONS (for dropdowns) ====================
    const fetchPositionOptions = async () => {
        try {
            const res = await axiosInstance.get('/api/options-contract-management/positions?per_page=200');
            if (res.data.success) {
                setPositionOptions(res.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch position options:', err);
        }
    };

    // ==================== FORMAT HELPERS ====================
    const formatNumber = (num) => num != null ? parseFloat(num).toLocaleString() : '-';
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatPercent = (val) => val != null ? `${(parseFloat(val) * 100).toFixed(2)}%` : '-';
    const formatDecimal = (val, digits = 4) => val != null ? parseFloat(val).toFixed(digits) : '-';
    const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';

    const renderPnlValue = (value) => (
        <Typography sx={{ color: value >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600, fontFamily, fontSize: '0.875rem' }}>
            {value != null ? `${value >= 0 ? '+' : ''}${parseFloat(value).toFixed(2)}` : '-'}
        </Typography>
    );

    // ==================== TAB 0: ACTIVE POSITIONS HANDLERS ====================

    const fetchPositions = useCallback(async () => {
        if (!canView) return;
        setPositionsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: positionsPage + 1, per_page: positionsPerPage });
            if (positionsSearch) params.append('search', positionsSearch);
            if (positionsStatusFilter) params.append('status', positionsStatusFilter);
            if (positionsStrategyFilter) params.append('strategy_type', positionsStrategyFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/positions?${params.toString()}`);
            if (res.data.success) {
                setPositionsData(res.data.data || []);
                setPositionsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch positions');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch positions');
        } finally {
            setPositionsLoading(false);
        }
    }, [canView, positionsPage, positionsPerPage, positionsSearch, positionsStatusFilter, positionsStrategyFilter]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchPositions();
    }, [fetchPositions, canView, activeTab]);

    const getDefaultLeg = () => ({
        side: '',
        option_type: '',
        strike_price: '',
        expiration_date: '',
        quantity: '',
        entry_price: '',
        delta: '',
        gamma: '',
        theta: '',
        vega: '',
    });

    const addLeg = () => {
        setFormLegs([...formLegs, getDefaultLeg()]);
    };

    const removeLeg = (idx) => {
        setFormLegs(formLegs.filter((_, i) => i !== idx));
    };

    const updateLeg = (idx, field, value) => {
        const updated = [...formLegs];
        updated[idx] = { ...updated[idx], [field]: value };
        setFormLegs(updated);
    };

    const handlePositionsOpenDialog = (row = null) => {
        if (row) {
            setPositionsEditing(row);
            const matchedSymbol = symbolOptions.find(s => s.symbol === row.symbol) || null;
            setPositionsForm({
                symbol_id: matchedSymbol,
                strategy_type: row.strategy_type || '',
                position_name: row.position_name || '',
                open_date: row.open_date || '',
                underlying_price_at_open: row.underlying_price_at_open ?? '',
                notes: row.notes || '',
            });
            setFormLegs([]);
        } else {
            setPositionsEditing(null);
            setPositionsForm({
                symbol_id: null,
                strategy_type: '',
                position_name: '',
                open_date: '',
                underlying_price_at_open: '',
                notes: '',
            });
            setFormLegs([getDefaultLeg()]);
        }
        setPositionsDialog(true);
    };

    const handlePositionsSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                symbol_id: positionsForm.symbol_id?.id || positionsForm.symbol_id,
                strategy_type: positionsForm.strategy_type,
                position_name: positionsForm.position_name,
                open_date: positionsForm.open_date,
                underlying_price_at_open: positionsForm.underlying_price_at_open ? parseFloat(positionsForm.underlying_price_at_open) : null,
                notes: positionsForm.notes,
            };
            if (!positionsEditing) {
                payload.legs = formLegs.map(leg => ({
                    side: leg.side,
                    option_type: leg.option_type,
                    strike_price: leg.strike_price ? parseFloat(leg.strike_price) : null,
                    expiration_date: leg.expiration_date,
                    quantity: leg.quantity ? parseInt(leg.quantity) : null,
                    entry_price: leg.entry_price ? parseFloat(leg.entry_price) : null,
                    delta: leg.delta ? parseFloat(leg.delta) : null,
                    gamma: leg.gamma ? parseFloat(leg.gamma) : null,
                    theta: leg.theta ? parseFloat(leg.theta) : null,
                    vega: leg.vega ? parseFloat(leg.vega) : null,
                }));
            }
            let res;
            if (positionsEditing) {
                res = await axiosInstance.put(`/api/options-contract-management/positions/${positionsEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/options-contract-management/positions', payload);
            }
            if (res.data.success) {
                setPositionsDialog(false);
                fetchPositions();
                fetchPositionOptions();
            } else {
                setError(res.data.message || 'Failed to save position');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save position');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePositionsDelete = async () => {
        if (!positionsDeleteTarget) return;
        try {
            const res = await axiosInstance.delete(`/api/options-contract-management/positions/${positionsDeleteTarget.id}`);
            if (res.data.success) {
                setPositionsDeleteDialog(false);
                setPositionsDeleteTarget(null);
                fetchPositions();
                fetchPositionOptions();
            } else {
                setError(res.data.message || 'Failed to delete position');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete position');
        }
    };

    const handleRefreshGreeks = async (positionId) => {
        setRefreshingGreeksId(positionId);
        try {
            const res = await axiosInstance.post(`/api/options-contract-management/positions/${positionId}/refresh-greeks`);
            if (res.data.success) {
                fetchPositions();
            } else {
                setError(res.data.message || 'Failed to refresh Greeks');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to refresh Greeks');
        } finally {
            setRefreshingGreeksId(null);
        }
    };

    const handleViewPosition = (positionId) => {
        setSelectedPositionId(positionId);
        setActiveTab(1);
    };

    // ==================== TAB 1: POSITION DETAIL HANDLERS ====================

    const fetchPositionDetail = useCallback(async () => {
        if (!canView || !selectedPositionId) return;
        setDetailLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/options-contract-management/positions/${selectedPositionId}`);
            if (res.data.success) {
                setDetailData(res.data.data || null);
            } else {
                setError(res.data.message || 'Failed to fetch position detail');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch position detail');
        } finally {
            setDetailLoading(false);
        }
    }, [canView, selectedPositionId]);

    useEffect(() => {
        if (canView && activeTab === 1 && selectedPositionId) fetchPositionDetail();
    }, [fetchPositionDetail, canView, activeTab, selectedPositionId]);

    const handleLegOpenDialog = (leg = null) => {
        if (leg) {
            setLegEditing(leg);
            setLegForm({
                side: leg.side || '',
                option_type: leg.option_type || '',
                strike_price: leg.strike_price ?? '',
                expiration_date: leg.expiration_date || '',
                quantity: leg.quantity ?? '',
                entry_price: leg.entry_price ?? '',
                delta: leg.delta ?? '',
                gamma: leg.gamma ?? '',
                theta: leg.theta ?? '',
                vega: leg.vega ?? '',
            });
        } else {
            setLegEditing(null);
            setLegForm({
                side: '',
                option_type: '',
                strike_price: '',
                expiration_date: '',
                quantity: '',
                entry_price: '',
                delta: '',
                gamma: '',
                theta: '',
                vega: '',
            });
        }
        setLegDialog(true);
    };

    const handleLegSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                side: legForm.side,
                option_type: legForm.option_type,
                strike_price: legForm.strike_price ? parseFloat(legForm.strike_price) : null,
                expiration_date: legForm.expiration_date,
                quantity: legForm.quantity ? parseInt(legForm.quantity) : null,
                entry_price: legForm.entry_price ? parseFloat(legForm.entry_price) : null,
                delta: legForm.delta ? parseFloat(legForm.delta) : null,
                gamma: legForm.gamma ? parseFloat(legForm.gamma) : null,
                theta: legForm.theta ? parseFloat(legForm.theta) : null,
                vega: legForm.vega ? parseFloat(legForm.vega) : null,
            };
            let res;
            if (legEditing) {
                res = await axiosInstance.put(`/api/options-contract-management/positions/${selectedPositionId}/legs/${legEditing.id}`, payload);
            } else {
                res = await axiosInstance.post(`/api/options-contract-management/positions/${selectedPositionId}/legs`, payload);
            }
            if (res.data.success) {
                setLegDialog(false);
                fetchPositionDetail();
            } else {
                setError(res.data.message || 'Failed to save leg');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save leg');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLegDelete = async () => {
        if (!legDeleteTarget) return;
        try {
            const res = await axiosInstance.delete(`/api/options-contract-management/positions/${selectedPositionId}/legs/${legDeleteTarget.id}`);
            if (res.data.success) {
                setLegDeleteDialog(false);
                setLegDeleteTarget(null);
                fetchPositionDetail();
            } else {
                setError(res.data.message || 'Failed to delete leg');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete leg');
        }
    };

    // ==================== TAB 2: P&L ATTRIBUTION HANDLERS ====================

    const fetchPnlAttribution = useCallback(async () => {
        if (!canView || !pnlPositionId) return;
        setPnlLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: pnlPage + 1, per_page: pnlPerPage });
            const res = await axiosInstance.get(`/api/options-contract-management/pnl-attribution/${pnlPositionId}?${params.toString()}`);
            if (res.data.success) {
                setPnlData(res.data.data || []);
                setPnlTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch P&L attribution');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch P&L attribution');
        } finally {
            setPnlLoading(false);
        }
    }, [canView, pnlPositionId, pnlPage, pnlPerPage]);

    useEffect(() => {
        if (canView && activeTab === 2 && pnlPositionId) fetchPnlAttribution();
    }, [fetchPnlAttribution, canView, activeTab, pnlPositionId]);

    const handlePnlCalcOpen = () => {
        setPnlCalcForm({
            underlying_price_change: '',
            iv_change: '',
            time_decay_days: '',
            total_pnl_change: '',
        });
        setPnlCalcDialog(true);
    };

    const handlePnlCalcSave = async () => {
        if (!pnlPositionId) return;
        setSubmitting(true);
        try {
            const payload = {
                underlying_price_change: pnlCalcForm.underlying_price_change ? parseFloat(pnlCalcForm.underlying_price_change) : null,
                iv_change: pnlCalcForm.iv_change ? parseFloat(pnlCalcForm.iv_change) : null,
                time_decay_days: pnlCalcForm.time_decay_days ? parseFloat(pnlCalcForm.time_decay_days) : null,
                total_pnl_change: pnlCalcForm.total_pnl_change ? parseFloat(pnlCalcForm.total_pnl_change) : null,
            };
            const res = await axiosInstance.post(`/api/options-contract-management/pnl-attribution/${pnlPositionId}`, payload);
            if (res.data.success) {
                setPnlCalcDialog(false);
                fetchPnlAttribution();
            } else {
                setError(res.data.message || 'Failed to calculate P&L attribution');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to calculate P&L attribution');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== TAB 3: MARGIN REQUIREMENTS HANDLERS ====================

    const handleMarginCalcOpen = () => {
        setMarginCalcForm({
            methodology: '',
        });
        setMarginCalcDialog(true);
    };

    const handleMarginCalcSave = async () => {
        if (!marginPositionId) return;
        setMarginLoading(true);
        setSubmitting(true);
        try {
            const payload = {
                methodology: marginCalcForm.methodology,
            };
            const res = await axiosInstance.post(`/api/options-contract-management/margin/${marginPositionId}/calculate`, payload);
            if (res.data.success) {
                setMarginData(res.data.data || null);
                setMarginCalcDialog(false);
            } else {
                setError(res.data.message || 'Failed to calculate margin');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to calculate margin');
        } finally {
            setMarginLoading(false);
            setSubmitting(false);
        }
    };

    // ==================== TAB 4: ROLL MANAGEMENT HANDLERS ====================

    const fetchRollRecommendations = useCallback(async () => {
        if (!canView) return;
        setRollLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: rollPage + 1, per_page: rollPerPage });
            if (rollStatusFilter) params.append('status', rollStatusFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/roll-recommendations?${params.toString()}`);
            if (res.data.success) {
                setRollData(res.data.data || []);
                setRollTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch roll recommendations');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch roll recommendations');
        } finally {
            setRollLoading(false);
        }
    }, [canView, rollPage, rollPerPage, rollStatusFilter]);

    useEffect(() => {
        if (canView && activeTab === 4) fetchRollRecommendations();
    }, [fetchRollRecommendations, canView, activeTab]);

    const handleRollEvalOpen = () => {
        setRollEvalPositionId(null);
        setRollEvalDialog(true);
    };

    const handleRollEvalSave = async () => {
        if (!rollEvalPositionId) return;
        setEvaluatingRoll(true);
        try {
            const res = await axiosInstance.post(`/api/options-contract-management/roll-recommendations/${rollEvalPositionId}`);
            if (res.data.success) {
                setRollEvalDialog(false);
                fetchRollRecommendations();
            } else {
                setError(res.data.message || 'Failed to evaluate roll');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to evaluate roll');
        } finally {
            setEvaluatingRoll(false);
        }
    };

    const handleRollStatusOpen = (row) => {
        setRollStatusTarget(row);
        setRollStatusValue('');
        setRollStatusDialog(true);
    };

    const handleRollStatusSave = async () => {
        if (!rollStatusTarget || !rollStatusValue) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.put(`/api/options-contract-management/roll-recommendations/${rollStatusTarget.id}/status`, {
                status: rollStatusValue,
            });
            if (res.data.success) {
                setRollStatusDialog(false);
                setRollStatusTarget(null);
                fetchRollRecommendations();
            } else {
                setError(res.data.message || 'Failed to update roll status');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update roll status');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== TAB 5: ASSIGNMENT MONITOR HANDLERS ====================

    const fetchAssignmentMonitors = useCallback(async () => {
        if (!canView) return;
        setAssignmentLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: assignmentPage + 1, per_page: assignmentPerPage });
            if (assignmentPositionFilter) params.append('position_id', assignmentPositionFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/assignment-monitors?${params.toString()}`);
            if (res.data.success) {
                setAssignmentData(res.data.data || []);
                setAssignmentTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch assignment monitors');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch assignment monitors');
        } finally {
            setAssignmentLoading(false);
        }
    }, [canView, assignmentPage, assignmentPerPage, assignmentPositionFilter]);

    useEffect(() => {
        if (canView && activeTab === 5) fetchAssignmentMonitors();
    }, [fetchAssignmentMonitors, canView, activeTab]);

    const handleAssignmentCheckOpen = () => {
        setAssignmentCheckPositionId(null);
        setAssignmentCheckDialog(true);
    };

    const handleAssignmentCheckSave = async () => {
        if (!assignmentCheckPositionId) return;
        setCheckingAssignment(true);
        try {
            const res = await axiosInstance.post(`/api/options-contract-management/assignment-monitors/${assignmentCheckPositionId}/check`);
            if (res.data.success) {
                setAssignmentCheckDialog(false);
                fetchAssignmentMonitors();
            } else {
                setError(res.data.message || 'Failed to check assignment risk');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to check assignment risk');
        } finally {
            setCheckingAssignment(false);
        }
    };

    // ==================== PERMISSION GATE ====================

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ fontFamily }}>
                    You do not have permission to view Position Management.
                </Alert>
            </Box>
        );
    }

    // ==================== TAB 0 RENDER: ACTIVE POSITIONS ====================

    const renderActivePositions = () => (
        <>
            {/* Filters & Actions Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search Symbol"
                            value={positionsSearch}
                            onChange={(e) => { setPositionsSearch(e.target.value); setPositionsPage(0); }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={positionsStatusFilter}
                                label="Status"
                                onChange={(e) => { setPositionsStatusFilter(e.target.value); setPositionsPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(PositionStatus).map(s => (
                                    <MenuItem key={s} value={s}>{PositionStatusLabels[s]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Strategy</InputLabel>
                            <Select
                                value={positionsStrategyFilter}
                                label="Strategy"
                                onChange={(e) => { setPositionsStrategyFilter(e.target.value); setPositionsPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(StrategyType).map(s => (
                                    <MenuItem key={s} value={s}>{StrategyTypeLabels[s]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchPositions}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handlePositionsOpenDialog()}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Add Position
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Positions Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Position Name</TableCell>
                            <TableCell>Strategy</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Open Date</TableCell>
                            <TableCell align="right">Legs</TableCell>
                            <TableCell align="right">Net Premium</TableCell>
                            <TableCell align="right">Unrealized P&L</TableCell>
                            <TableCell align="right">Total P&L</TableCell>
                            <TableCell>Nearest Exp</TableCell>
                            <TableCell align="right">DTE</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {positionsLoading ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : positionsData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                                    <TrendingUpIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No positions found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            positionsData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{row.position_name || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={StrategyTypeLabels[row.strategy_type] || row.strategy_type || '-'}
                                            color={StrategyTypeColors[row.strategy_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={PositionStatusLabels[row.status] || row.status || '-'}
                                            color={PositionStatusColors[row.status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(row.open_date)}</TableCell>
                                    <TableCell align="right">{row.num_legs ?? '-'}</TableCell>
                                    <TableCell align="right">{formatPrice(row.net_premium)}</TableCell>
                                    <TableCell align="right">{renderPnlValue(row.unrealized_pnl)}</TableCell>
                                    <TableCell align="right">{renderPnlValue(row.total_pnl)}</TableCell>
                                    <TableCell>{formatDate(row.nearest_expiration)}</TableCell>
                                    <TableCell align="right">{row.days_to_nearest_expiry ?? '-'}</TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                            <Tooltip title="View Detail">
                                                <IconButton size="small" onClick={() => handleViewPosition(row.id)}>
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Refresh Greeks">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRefreshGreeks(row.id)}
                                                    disabled={refreshingGreeksId === row.id}
                                                >
                                                    {refreshingGreeksId === row.id ? <CircularProgress size={18} /> : <CalculateIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            {canEdit && (
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handlePositionsOpenDialog(row)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canDelete && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => { setPositionsDeleteTarget(row); setPositionsDeleteDialog(true); }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={positionsTotal}
                page={positionsPage}
                onPageChange={(_, p) => setPositionsPage(p)}
                rowsPerPage={positionsPerPage}
                onRowsPerPageChange={(e) => { setPositionsPerPage(parseInt(e.target.value, 10)); setPositionsPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </>
    );

    // ==================== TAB 1 RENDER: POSITION DETAIL ====================

    const renderPositionDetail = () => {
        if (!selectedPositionId) {
            return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <ViewIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                    <Typography color="text.secondary" sx={{ fontFamily }}>
                        Select a position from the Active Positions tab to view details.
                    </Typography>
                    <Button
                        sx={{ mt: 2, fontFamily, textTransform: 'none' }}
                        onClick={() => setActiveTab(0)}
                    >
                        Go to Active Positions
                    </Button>
                </Paper>
            );
        }

        if (detailLoading) {
            return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;
        }

        if (!detailData) {
            return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ fontFamily }}>Position data not available.</Typography>
                </Paper>
            );
        }

        const pos = detailData;
        const legs = pos.legs || [];
        const greeks = pos.net_greeks || {};

        return (
            <>
                {/* Position Header Card */}
                <Paper sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Symbol</Typography>
                            <Typography fontWeight={700} sx={{ fontFamily }}>{pos.symbol || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Position Name</Typography>
                            <Typography fontWeight={600} sx={{ fontFamily }}>{pos.position_name || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Strategy</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <Chip
                                    label={StrategyTypeLabels[pos.strategy_type] || pos.strategy_type || '-'}
                                    color={StrategyTypeColors[pos.strategy_type] || 'default'}
                                    size="small"
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Status</Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <Chip
                                    label={PositionStatusLabels[pos.status] || pos.status || '-'}
                                    color={PositionStatusColors[pos.status] || 'default'}
                                    size="small"
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Open Date</Typography>
                            <Typography sx={{ fontFamily }}>{formatDate(pos.open_date)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Underlying Price at Open</Typography>
                            <Typography sx={{ fontFamily }}>{formatPrice(pos.underlying_price_at_open)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Net Premium</Typography>
                            <Typography sx={{ fontFamily }}>{formatPrice(pos.net_premium)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Total P&L</Typography>
                            {renderPnlValue(pos.total_pnl)}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Unrealized P&L</Typography>
                            {renderPnlValue(pos.unrealized_pnl)}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Nearest Expiration</Typography>
                            <Typography sx={{ fontFamily }}>{formatDate(pos.nearest_expiration)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Days to Nearest Expiry</Typography>
                            <Typography sx={{ fontFamily }}>{pos.days_to_nearest_expiry ?? '-'}</Typography>
                        </Grid>
                        {pos.notes && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Notes</Typography>
                                <Typography sx={{ fontFamily }}>{pos.notes}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* Legs Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ fontFamily }}>Position Legs</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchPositionDetail}><RefreshIcon /></IconButton>
                        </Tooltip>
                        {canAdd && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleLegOpenDialog()}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Add Leg
                            </Button>
                        )}
                    </Box>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                                <TableCell>#</TableCell>
                                <TableCell>Side</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Strike</TableCell>
                                <TableCell>Expiration</TableCell>
                                <TableCell align="right">Qty</TableCell>
                                <TableCell align="right">Entry Price</TableCell>
                                <TableCell align="right">Current Price</TableCell>
                                <TableCell align="right">Delta</TableCell>
                                <TableCell align="right">Gamma</TableCell>
                                <TableCell align="right">Theta</TableCell>
                                <TableCell align="right">Vega</TableCell>
                                <TableCell align="right">Unrealized P&L</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {legs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary" sx={{ fontFamily }}>No legs found for this position</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                legs.map((leg, idx) => (
                                    <TableRow key={leg.id || idx} hover sx={{ '& td': { fontFamily } }}>
                                        <TableCell>{leg.leg_order ?? idx + 1}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={LegSideLabels[leg.side] || leg.side || '-'}
                                                color={LegSideColors[leg.side] || 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={OptionTypeLabels[leg.option_type] || leg.option_type || '-'}
                                                color={OptionTypeColors[leg.option_type] || 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">{formatPrice(leg.strike_price)}</TableCell>
                                        <TableCell>{formatDate(leg.expiration_date)}</TableCell>
                                        <TableCell align="right">{leg.quantity ?? '-'}</TableCell>
                                        <TableCell align="right">{formatPrice(leg.entry_price)}</TableCell>
                                        <TableCell align="right">{formatPrice(leg.current_price)}</TableCell>
                                        <TableCell align="right">{formatDecimal(leg.delta)}</TableCell>
                                        <TableCell align="right">{formatDecimal(leg.gamma)}</TableCell>
                                        <TableCell align="right">{formatDecimal(leg.theta)}</TableCell>
                                        <TableCell align="right">{formatDecimal(leg.vega)}</TableCell>
                                        <TableCell align="right">{renderPnlValue(leg.unrealized_pnl)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={PositionStatusLabels[leg.status] || leg.status || '-'}
                                                color={PositionStatusColors[leg.status] || 'default'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                {canEdit && (
                                                    <Tooltip title="Edit Leg">
                                                        <IconButton size="small" onClick={() => handleLegOpenDialog(leg)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete Leg">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => { setLegDeleteTarget(leg); setLegDeleteDialog(true); }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Net Greeks Summary Card */}
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily, mb: 2 }}>Net Greeks Summary</Typography>
                <Paper sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Net Delta</Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ fontFamily }}>{formatDecimal(greeks.net_delta)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Net Gamma</Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ fontFamily }}>{formatDecimal(greeks.net_gamma)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Net Theta</Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ fontFamily }}>{formatDecimal(greeks.net_theta)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Net Vega</Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ fontFamily }}>{formatDecimal(greeks.net_vega)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Net Rho</Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ fontFamily }}>{formatDecimal(greeks.net_rho)}</Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </>
        );
    };

    // ==================== TAB 2 RENDER: P&L ATTRIBUTION ====================

    const renderPnlAttribution = () => (
        <>
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Select Position</InputLabel>
                            <Select
                                value={pnlPositionId || ''}
                                label="Select Position"
                                onChange={(e) => { setPnlPositionId(e.target.value); setPnlPage(0); }}
                            >
                                <MenuItem value="">-- Select --</MenuItem>
                                {positionOptions.map(p => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.symbol} - {p.position_name || `Position #${p.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchPnlAttribution} disabled={!pnlPositionId}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && pnlPositionId && (
                            <Button
                                variant="contained"
                                startIcon={<CalculateIcon />}
                                onClick={handlePnlCalcOpen}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Calculate Attribution
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {!pnlPositionId ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CalculateIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                    <Typography color="text.secondary" sx={{ fontFamily }}>Select a position to view P&L attribution data.</Typography>
                </Paper>
            ) : (
                <>
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Total P&L Change</TableCell>
                                    <TableCell align="right">Delta P&L</TableCell>
                                    <TableCell align="right">Gamma P&L</TableCell>
                                    <TableCell align="right">Theta P&L</TableCell>
                                    <TableCell align="right">Vega P&L</TableCell>
                                    <TableCell align="right">Rho P&L</TableCell>
                                    <TableCell align="right">Residual P&L</TableCell>
                                    <TableCell>Dominant Driver</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pnlLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                    </TableRow>
                                ) : pnlData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary" sx={{ fontFamily }}>No P&L attribution data found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pnlData.map(row => (
                                        <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                            <TableCell>{formatDate(row.attribution_date)}</TableCell>
                                            <TableCell align="right">{renderPnlValue(row.total_pnl_change)}</TableCell>
                                            <TableCell align="right">{renderPnlValue(row.delta_pnl)}</TableCell>
                                            <TableCell align="right">{renderPnlValue(row.gamma_pnl)}</TableCell>
                                            <TableCell align="right">{renderPnlValue(row.theta_pnl)}</TableCell>
                                            <TableCell align="right">{renderPnlValue(row.vega_pnl)}</TableCell>
                                            <TableCell align="right">{renderPnlValue(row.rho_pnl)}</TableCell>
                                            <TableCell align="right">{renderPnlValue(row.residual_pnl)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.dominant_driver || '-'}
                                                    color={
                                                        row.dominant_driver === 'delta' ? 'primary' :
                                                        row.dominant_driver === 'gamma' ? 'info' :
                                                        row.dominant_driver === 'theta' ? 'warning' :
                                                        row.dominant_driver === 'vega' ? 'secondary' :
                                                        row.dominant_driver === 'rho' ? 'success' :
                                                        'default'
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={pnlTotal}
                        page={pnlPage}
                        onPageChange={(_, p) => setPnlPage(p)}
                        rowsPerPage={pnlPerPage}
                        onRowsPerPageChange={(e) => { setPnlPerPage(parseInt(e.target.value, 10)); setPnlPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </>
            )}
        </>
    );

    // ==================== TAB 3 RENDER: MARGIN REQUIREMENTS ====================

    const renderMarginRequirements = () => (
        <>
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Select Position</InputLabel>
                            <Select
                                value={marginPositionId || ''}
                                label="Select Position"
                                onChange={(e) => { setMarginPositionId(e.target.value); setMarginData(null); }}
                            >
                                <MenuItem value="">-- Select --</MenuItem>
                                {positionOptions.map(p => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.symbol} - {p.position_name || `Position #${p.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && marginPositionId && (
                            <Button
                                variant="contained"
                                startIcon={<CalculateIcon />}
                                onClick={handleMarginCalcOpen}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Calculate Margin
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {!marginPositionId ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CalculateIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                    <Typography color="text.secondary" sx={{ fontFamily }}>Select a position and calculate margin requirements.</Typography>
                </Paper>
            ) : marginLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            ) : !marginData ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ fontFamily }}>
                        No margin data available. Click "Calculate Margin" to compute requirements.
                    </Typography>
                </Paper>
            ) : (
                <Paper sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ fontFamily, mb: 3 }}>Margin Calculation Results</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Initial Margin</Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ fontFamily }}>{formatPrice(marginData.initial_margin)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Maintenance Margin</Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ fontFamily }}>{formatPrice(marginData.maintenance_margin)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Buying Power Effect</Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ fontFamily }}>{formatPrice(marginData.buying_power_effect)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Cross Margin Credit</Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ fontFamily }}>{formatPrice(marginData.cross_margin_credit)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Net Margin After Netting</Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ fontFamily }}>{formatPrice(marginData.net_margin_after_netting)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>Margin Utilization</Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ fontFamily }}>{formatPercent(marginData.margin_utilization_pct)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" fontWeight={600} sx={{ fontFamily }}>Margin Call:</Typography>
                                <Chip
                                    label={marginData.is_margin_call ? 'YES - Margin Call' : 'No Margin Call'}
                                    color={marginData.is_margin_call ? 'error' : 'success'}
                                    size="small"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}
        </>
    );

    // ==================== TAB 4 RENDER: ROLL MANAGEMENT ====================

    const renderRollManagement = () => (
        <>
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={rollStatusFilter}
                                label="Status"
                                onChange={(e) => { setRollStatusFilter(e.target.value); setRollPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(RollStatus).map(s => (
                                    <MenuItem key={s} value={s}>{RollStatusLabels[s]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchRollRecommendations}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<CalculateIcon />}
                                onClick={handleRollEvalOpen}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Evaluate Roll
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Position</TableCell>
                            <TableCell>Original Exp</TableCell>
                            <TableCell>Recommended Exp</TableCell>
                            <TableCell align="right">Original Strike</TableCell>
                            <TableCell align="right">Recommended Strike</TableCell>
                            <TableCell align="right">Credit/Debit</TableCell>
                            <TableCell align="right">Time Value Remaining</TableCell>
                            <TableCell align="right">Theta Decay Accel</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rollLoading ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : rollData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No roll recommendations found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rollData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>
                                            {row.position_symbol || row.symbol || `#${row.position_id}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{formatDate(row.original_expiration)}</TableCell>
                                    <TableCell>{formatDate(row.recommended_expiration)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.original_strike)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.recommended_strike)}</TableCell>
                                    <TableCell align="right">{renderPnlValue(row.roll_credit_debit)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.time_value_remaining_pct)}</TableCell>
                                    <TableCell align="right">{formatDecimal(row.theta_decay_acceleration)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={RollStatusLabels[row.status] || row.status || '-'}
                                            color={RollStatusColors[row.status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                            {canEdit && (
                                                <Tooltip title="Update Status">
                                                    <IconButton size="small" onClick={() => handleRollStatusOpen(row)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rollTotal}
                page={rollPage}
                onPageChange={(_, p) => setRollPage(p)}
                rowsPerPage={rollPerPage}
                onRowsPerPageChange={(e) => { setRollPerPage(parseInt(e.target.value, 10)); setRollPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </>
    );

    // ==================== TAB 5 RENDER: ASSIGNMENT MONITOR ====================

    const renderAssignmentMonitor = () => (
        <>
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Position</InputLabel>
                            <Select
                                value={assignmentPositionFilter || ''}
                                label="Filter by Position"
                                onChange={(e) => { setAssignmentPositionFilter(e.target.value || null); setAssignmentPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {positionOptions.map(p => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.symbol} - {p.position_name || `Position #${p.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchAssignmentMonitors}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<CalculateIcon />}
                                onClick={handleAssignmentCheckOpen}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Check Assignment
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Strike</TableCell>
                            <TableCell>Expiration</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Side</TableCell>
                            <TableCell>ITM</TableCell>
                            <TableCell align="right">ITM Amount</TableCell>
                            <TableCell align="right">ITM %</TableCell>
                            <TableCell>Early Exercise Risk</TableCell>
                            <TableCell>Recommended Action</TableCell>
                            <TableCell>Rationale</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {assignmentLoading ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : assignmentData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No assignment monitor data found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            assignmentData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={OptionTypeLabels[row.option_type] || row.option_type || '-'}
                                            color={OptionTypeColors[row.option_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={LegSideLabels[row.side] || row.side || '-'}
                                            color={LegSideColors[row.side] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.is_itm ? 'ITM' : 'OTM'}
                                            color={row.is_itm ? 'error' : 'success'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.itm_amount)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.itm_pct)}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                                            <Box sx={{
                                                flex: 1,
                                                height: 8,
                                                borderRadius: 4,
                                                bgcolor: '#e2e8f0',
                                                overflow: 'hidden',
                                            }}>
                                                <Box sx={{
                                                    width: `${Math.min((row.early_exercise_risk || 0) * 100, 100)}%`,
                                                    height: '100%',
                                                    borderRadius: 4,
                                                    bgcolor:
                                                        (row.early_exercise_risk || 0) >= 0.7 ? '#ef4444' :
                                                        (row.early_exercise_risk || 0) >= 0.4 ? '#f59e0b' :
                                                        '#22c55e',
                                                }} />
                                            </Box>
                                            <Typography variant="caption" fontWeight={600} sx={{ fontFamily, minWidth: 35 }}>
                                                {row.early_exercise_risk != null ? `${(row.early_exercise_risk * 100).toFixed(0)}%` : '-'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.recommended_action || '-'}
                                            color={
                                                row.recommended_action === 'CLOSE' ? 'error' :
                                                row.recommended_action === 'ROLL' ? 'warning' :
                                                row.recommended_action === 'HOLD' ? 'success' :
                                                row.recommended_action === 'EXERCISE' ? 'primary' :
                                                'default'
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={row.action_rationale || ''}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily,
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {row.action_rationale || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={assignmentTotal}
                page={assignmentPage}
                onPageChange={(_, p) => setAssignmentPage(p)}
                rowsPerPage={assignmentPerPage}
                onRowsPerPageChange={(e) => { setAssignmentPerPage(parseInt(e.target.value, 10)); setAssignmentPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </>
    );

    // ==================== MAIN RENDER ====================

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Box sx={{ background: 'linear-gradient(135deg, #1e1e2e 0%, #2d1b4e 100%)', borderRadius: 3, p: 4, mb: 3, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: `${ACCENT}15` }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: '30%', width: 100, height: 100, borderRadius: '50%', background: `${ACCENT}10` }} />
                <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', fontFamily }}>Position Management</Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontFamily, mt: 1 }}>
                    Multi-leg positions, P&L attribution, margin, roll management, and assignment monitoring
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3, fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    <Tab label="Active Positions" />
                    <Tab label="Position Detail" />
                    <Tab label="P&L Attribution" />
                    <Tab label="Margin Requirements" />
                    <Tab label="Roll Management" />
                    <Tab label="Assignment Monitor" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderActivePositions()}
            {activeTab === 1 && renderPositionDetail()}
            {activeTab === 2 && renderPnlAttribution()}
            {activeTab === 3 && renderMarginRequirements()}
            {activeTab === 4 && renderRollManagement()}
            {activeTab === 5 && renderAssignmentMonitor()}

            {/* ==================== DIALOGS ==================== */}

            {/* Create/Edit Position Dialog */}
            <Dialog open={positionsDialog} onClose={() => setPositionsDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {positionsEditing ? 'Edit Position' : 'Create New Position'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={symbolOptions}
                                getOptionLabel={(opt) => opt.symbol || ''}
                                value={positionsForm.symbol_id || null}
                                onChange={(_, val) => setPositionsForm({ ...positionsForm, symbol_id: val })}
                                renderInput={(params) => <TextField {...params} label="Symbol" size="small" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Strategy Type</InputLabel>
                                <Select
                                    value={positionsForm.strategy_type || ''}
                                    label="Strategy Type"
                                    onChange={(e) => setPositionsForm({ ...positionsForm, strategy_type: e.target.value })}
                                >
                                    {Object.values(StrategyType).map(s => (
                                        <MenuItem key={s} value={s}>{StrategyTypeLabels[s]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Position Name"
                                value={positionsForm.position_name || ''}
                                onChange={(e) => setPositionsForm({ ...positionsForm, position_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Open Date"
                                type="date"
                                value={positionsForm.open_date || ''}
                                onChange={(e) => setPositionsForm({ ...positionsForm, open_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Underlying Price at Open"
                                type="number"
                                value={positionsForm.underlying_price_at_open ?? ''}
                                onChange={(e) => setPositionsForm({ ...positionsForm, underlying_price_at_open: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Notes"
                                multiline
                                rows={2}
                                value={positionsForm.notes || ''}
                                onChange={(e) => setPositionsForm({ ...positionsForm, notes: e.target.value })}
                            />
                        </Grid>
                    </Grid>

                    {/* Legs Section (only for new positions) */}
                    {!positionsEditing && (
                        <>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2, fontFamily }}>Position Legs</Typography>
                            {formLegs.map((leg, idx) => (
                                <Paper key={idx} sx={{ p: 2, mb: 1, border: '1px solid #e0e0e0' }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Side</InputLabel>
                                                <Select
                                                    value={leg.side}
                                                    label="Side"
                                                    onChange={(e) => updateLeg(idx, 'side', e.target.value)}
                                                >
                                                    {Object.entries(LegSideLabels).map(([k, v]) => (
                                                        <MenuItem key={k} value={k}>{v}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={leg.option_type}
                                                    label="Type"
                                                    onChange={(e) => updateLeg(idx, 'option_type', e.target.value)}
                                                >
                                                    {Object.entries(OptionTypeLabels).map(([k, v]) => (
                                                        <MenuItem key={k} value={k}>{v}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} sm={1.5}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Strike"
                                                type="number"
                                                value={leg.strike_price}
                                                onChange={(e) => updateLeg(idx, 'strike_price', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Expiration"
                                                type="date"
                                                value={leg.expiration_date}
                                                onChange={(e) => updateLeg(idx, 'expiration_date', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={4} sm={1}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Qty"
                                                type="number"
                                                value={leg.quantity}
                                                onChange={(e) => updateLeg(idx, 'quantity', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={4} sm={1.5}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Entry $"
                                                type="number"
                                                value={leg.entry_price}
                                                onChange={(e) => updateLeg(idx, 'entry_price', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={3} sm={1}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Delta"
                                                type="number"
                                                value={leg.delta}
                                                onChange={(e) => updateLeg(idx, 'delta', e.target.value)}
                                                inputProps={{ step: 0.01 }}
                                            />
                                        </Grid>
                                        <Grid item xs={3} sm={1}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Gamma"
                                                type="number"
                                                value={leg.gamma}
                                                onChange={(e) => updateLeg(idx, 'gamma', e.target.value)}
                                                inputProps={{ step: 0.001 }}
                                            />
                                        </Grid>
                                        <Grid item xs={3} sm={1}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Theta"
                                                type="number"
                                                value={leg.theta}
                                                onChange={(e) => updateLeg(idx, 'theta', e.target.value)}
                                                inputProps={{ step: 0.01 }}
                                            />
                                        </Grid>
                                        <Grid item xs={3} sm={1}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Vega"
                                                type="number"
                                                value={leg.vega}
                                                onChange={(e) => updateLeg(idx, 'vega', e.target.value)}
                                                inputProps={{ step: 0.01 }}
                                            />
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton color="error" onClick={() => removeLeg(idx)}><DeleteIcon /></IconButton>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                            <Button startIcon={<AddIcon />} onClick={addLeg} sx={{ mt: 1, fontFamily, textTransform: 'none' }}>
                                Add Leg
                            </Button>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setPositionsDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handlePositionsSave}
                        disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : (positionsEditing ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Position Dialog */}
            <Dialog open={positionsDeleteDialog} onClose={() => setPositionsDeleteDialog(false)}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Position</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete position "{positionsDeleteTarget?.position_name || positionsDeleteTarget?.symbol}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setPositionsDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handlePositionsDelete}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Leg Dialog */}
            <Dialog open={legDialog} onClose={() => setLegDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {legEditing ? 'Edit Leg' : 'Add Leg'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Side</InputLabel>
                                <Select
                                    value={legForm.side || ''}
                                    label="Side"
                                    onChange={(e) => setLegForm({ ...legForm, side: e.target.value })}
                                >
                                    {Object.entries(LegSideLabels).map(([k, v]) => (
                                        <MenuItem key={k} value={k}>{v}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Option Type</InputLabel>
                                <Select
                                    value={legForm.option_type || ''}
                                    label="Option Type"
                                    onChange={(e) => setLegForm({ ...legForm, option_type: e.target.value })}
                                >
                                    {Object.entries(OptionTypeLabels).map(([k, v]) => (
                                        <MenuItem key={k} value={k}>{v}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Strike Price"
                                type="number"
                                value={legForm.strike_price ?? ''}
                                onChange={(e) => setLegForm({ ...legForm, strike_price: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Expiration Date"
                                type="date"
                                value={legForm.expiration_date || ''}
                                onChange={(e) => setLegForm({ ...legForm, expiration_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Quantity"
                                type="number"
                                value={legForm.quantity ?? ''}
                                onChange={(e) => setLegForm({ ...legForm, quantity: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Entry Price"
                                type="number"
                                value={legForm.entry_price ?? ''}
                                onChange={(e) => setLegForm({ ...legForm, entry_price: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Delta"
                                type="number"
                                value={legForm.delta ?? ''}
                                onChange={(e) => setLegForm({ ...legForm, delta: e.target.value })}
                                inputProps={{ step: 0.01 }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Gamma"
                                type="number"
                                value={legForm.gamma ?? ''}
                                onChange={(e) => setLegForm({ ...legForm, gamma: e.target.value })}
                                inputProps={{ step: 0.001 }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Theta"
                                type="number"
                                value={legForm.theta ?? ''}
                                onChange={(e) => setLegForm({ ...legForm, theta: e.target.value })}
                                inputProps={{ step: 0.01 }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Vega"
                                type="number"
                                value={legForm.vega ?? ''}
                                onChange={(e) => setLegForm({ ...legForm, vega: e.target.value })}
                                inputProps={{ step: 0.01 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setLegDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleLegSave}
                        disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : (legEditing ? 'Update' : 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Leg Dialog */}
            <Dialog open={legDeleteDialog} onClose={() => setLegDeleteDialog(false)}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Leg</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete this leg ({LegSideLabels[legDeleteTarget?.side]} {OptionTypeLabels[legDeleteTarget?.option_type]} {legDeleteTarget?.strike_price})?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setLegDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleLegDelete}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* P&L Calculate Attribution Dialog */}
            <Dialog open={pnlCalcDialog} onClose={() => setPnlCalcDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Calculate P&L Attribution</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Underlying Price Change"
                                type="number"
                                value={pnlCalcForm.underlying_price_change ?? ''}
                                onChange={(e) => setPnlCalcForm({ ...pnlCalcForm, underlying_price_change: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="IV Change"
                                type="number"
                                value={pnlCalcForm.iv_change ?? ''}
                                onChange={(e) => setPnlCalcForm({ ...pnlCalcForm, iv_change: e.target.value })}
                                inputProps={{ step: 0.01 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Time Decay (days)"
                                type="number"
                                value={pnlCalcForm.time_decay_days ?? ''}
                                onChange={(e) => setPnlCalcForm({ ...pnlCalcForm, time_decay_days: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Total P&L Change"
                                type="number"
                                value={pnlCalcForm.total_pnl_change ?? ''}
                                onChange={(e) => setPnlCalcForm({ ...pnlCalcForm, total_pnl_change: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setPnlCalcDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handlePnlCalcSave}
                        disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Calculate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Margin Calculate Dialog */}
            <Dialog open={marginCalcDialog} onClose={() => setMarginCalcDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Calculate Margin Requirements</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Methodology</InputLabel>
                                <Select
                                    value={marginCalcForm.methodology || ''}
                                    label="Methodology"
                                    onChange={(e) => setMarginCalcForm({ ...marginCalcForm, methodology: e.target.value })}
                                >
                                    {Object.values(MarginMethodology).map(m => (
                                        <MenuItem key={m} value={m}>{MarginMethodologyLabels[m]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setMarginCalcDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleMarginCalcSave}
                        disabled={submitting || !marginCalcForm.methodology}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Calculate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Evaluate Roll Dialog */}
            <Dialog open={rollEvalDialog} onClose={() => setRollEvalDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Evaluate Roll Opportunity</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Select Position</InputLabel>
                                <Select
                                    value={rollEvalPositionId || ''}
                                    label="Select Position"
                                    onChange={(e) => setRollEvalPositionId(e.target.value)}
                                >
                                    <MenuItem value="">-- Select --</MenuItem>
                                    {positionOptions.map(p => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.symbol} - {p.position_name || `Position #${p.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRollEvalDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRollEvalSave}
                        disabled={evaluatingRoll || !rollEvalPositionId}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {evaluatingRoll ? <CircularProgress size={20} /> : 'Evaluate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Update Roll Status Dialog */}
            <Dialog open={rollStatusDialog} onClose={() => setRollStatusDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Update Roll Status</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>New Status</InputLabel>
                                <Select
                                    value={rollStatusValue}
                                    label="New Status"
                                    onChange={(e) => setRollStatusValue(e.target.value)}
                                >
                                    <MenuItem value={RollStatus.EXECUTED}>{RollStatusLabels.EXECUTED}</MenuItem>
                                    <MenuItem value={RollStatus.SKIPPED}>{RollStatusLabels.SKIPPED}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRollStatusDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRollStatusSave}
                        disabled={submitting || !rollStatusValue}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Check Assignment Dialog */}
            <Dialog open={assignmentCheckDialog} onClose={() => setAssignmentCheckDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Check Assignment Risk</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Select Position</InputLabel>
                                <Select
                                    value={assignmentCheckPositionId || ''}
                                    label="Select Position"
                                    onChange={(e) => setAssignmentCheckPositionId(e.target.value)}
                                >
                                    <MenuItem value="">-- Select --</MenuItem>
                                    {positionOptions.map(p => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.symbol} - {p.position_name || `Position #${p.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setAssignmentCheckDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAssignmentCheckSave}
                        disabled={checkingAssignment || !assignmentCheckPositionId}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {checkingAssignment ? <CircularProgress size={20} /> : 'Check'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PositionManagementPage;
